'use client';

import { BigNum, SpotMarketConfig, WRAPPED_SOL_MINT } from '@drift-labs/sdk';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import {
	UIMarket,
	isValidAddressForChain,
	SOLANA_CHAIN_ID,
	BITCOIN_CHAIN_ID,
} from '@drift/common';
import { notify } from 'src/utils/notifications';
import useFunXyzActions, {
	type FunXyzAsset,
	createWithdrawalUda,
	fetchWithdrawalSupportedAssets,
	SOLANA_TARGET_ASSETS,
} from 'src/hooks/useFunXyzActions';
import { useWithdrawToUda } from 'src/hooks/driftActions/spot-management/useWithdrawToUda';
import { isAssetAllowed } from 'src/constants/funXyzChains';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';

export interface UniqueAsset {
	symbol: string;
	name: string;
	logo?: string;
	decimals: number;
	availableChainIds: string[];
	addressByChainId: Record<string, string>;
}

export interface ChainOption {
	id: string;
	name: string;
	isAvailable: boolean;
}

interface WithdrawToOtherDestinationsParams {
	selectedCollateralMarket: SpotMarketConfig;
	amountBigNum: BigNum;
}

export default function useWithdrawToOtherDestinations({
	selectedCollateralMarket,
	amountBigNum,
}: WithdrawToOtherDestinationsParams) {
	const { fetchQuote, clearQuote, quote, isGettingQuote } = useFunXyzActions();

	const subAccountId =
		useDriftAccountsStore((s) => {
			const account = s.accounts[s.currentUserKey ?? ''];
			return account?.userId;
		}) ?? 0;

	// Fetch all supported assets from Fun.xyz (no params)
	const [destinationAssets, setDestinationAssets] = useState<FunXyzAsset[]>([]);
	const [supportedSourceBanks, setSupportedSourceBanks] = useState<
		SpotMarketConfig[] | undefined
	>(undefined);

	useEffect(() => {
		fetchWithdrawalSupportedAssets()
			.then(({ solanaSourceSymbols, destinationAssets }) => {
				setDestinationAssets(destinationAssets);

				const symbolSet = new Set(solanaSourceSymbols);
				const ALLOWED_SOURCE_SYMBOLS = new Set(['USDC', 'USDT', 'SOL']);
				const filtered = UIMarket.spotMarkets.filter(
					(m) =>
						symbolSet.has(m.symbol.toUpperCase()) &&
						ALLOWED_SOURCE_SYMBOLS.has(m.symbol.toUpperCase())
				);
				setSupportedSourceBanks(filtered.length > 0 ? filtered : undefined);
			})
			.catch(() => {});
	}, []);

	// Build unique receiving asset list from non-Solana destination assets
	const uniqueAssets = useMemo(() => {
		const assetMap = new Map<string, UniqueAsset>();

		for (const asset of destinationAssets) {
			if (!isAssetAllowed(asset.chainId, asset.symbol)) continue;

			const normalizedSymbol = asset.symbol.toUpperCase();
			const existing = assetMap.get(normalizedSymbol);

			if (existing) {
				if (!existing.availableChainIds.includes(asset.chainId)) {
					existing.availableChainIds.push(asset.chainId);
				}
				if (asset.address) {
					existing.addressByChainId[asset.chainId] = asset.address;
				}
				if (!existing.logo && asset.logo) {
					existing.logo = asset.logo;
				}
			} else {
				assetMap.set(normalizedSymbol, {
					symbol: asset.symbol,
					name: asset.name,
					logo: asset.logo,
					decimals: asset.decimals,
					availableChainIds: [asset.chainId],
					addressByChainId: asset.address
						? { [asset.chainId]: asset.address }
						: {},
				});
			}
		}

		const assets = Array.from(assetMap.values());
		assets.sort((a, b) => {
			if (a.symbol.toUpperCase() === 'USDC') return -1;
			if (b.symbol.toUpperCase() === 'USDC') return 1;
			return a.symbol.localeCompare(b.symbol);
		});

		return assets;
	}, [destinationAssets]);

	// Receiving asset selection (independent from source)
	const [selectedReceivingAssetSymbol, setSelectedReceivingAssetSymbol] =
		useState<string | null>(null);

	// Default to source symbol when assets load
	useEffect(() => {
		if (uniqueAssets.length > 0 && !selectedReceivingAssetSymbol) {
			const sourceMatch = uniqueAssets.find(
				(a) =>
					a.symbol.toUpperCase() ===
					selectedCollateralMarket.symbol.toUpperCase()
			);
			const defaultAsset =
				sourceMatch ??
				uniqueAssets.find((a) => a.symbol.toUpperCase() === 'USDC') ??
				uniqueAssets[0];
			if (defaultAsset) {
				setSelectedReceivingAssetSymbol(defaultAsset.symbol);
			}
		}
	}, [
		uniqueAssets,
		selectedReceivingAssetSymbol,
		selectedCollateralMarket.symbol,
	]);

	// Reset receiving asset when source changes
	useEffect(() => {
		setSelectedReceivingAssetSymbol(null);
	}, [selectedCollateralMarket.symbol]);

	const effectiveReceivingSymbol =
		selectedReceivingAssetSymbol ?? selectedCollateralMarket.symbol;

	// Derive unique chains from destination assets
	const chains = useMemo(() => {
		const chainMap = new Map<string, { chainId: string; chainName: string }>();

		for (const asset of destinationAssets) {
			if (chainMap.has(asset.chainId)) continue;
			const displayName =
				asset.chainId === BITCOIN_CHAIN_ID
					? 'Bitcoin (Segwit)'
					: asset.chainName;
			chainMap.set(asset.chainId, {
				chainId: asset.chainId,
				chainName: displayName,
			});
		}
		return Array.from(chainMap.values());
	}, [destinationAssets]);

	// Build chain list with availability based on receiving asset
	const selectedReceivingAsset = useMemo(
		() =>
			uniqueAssets.find(
				(a) => a.symbol.toUpperCase() === effectiveReceivingSymbol.toUpperCase()
			) ?? null,
		[uniqueAssets, effectiveReceivingSymbol]
	);

	const chainOptions = useMemo(() => {
		const availableChainIds = new Set(
			selectedReceivingAsset?.availableChainIds ?? []
		);

		const chainList: ChainOption[] = chains.map((chain) => ({
			id: chain.chainId,
			name: chain.chainName,
			isAvailable: availableChainIds.has(chain.chainId),
		}));

		chainList.sort((a, b) => {
			if (a.isAvailable && !b.isAvailable) return -1;
			if (!a.isAvailable && b.isAvailable) return 1;
			return a.name.localeCompare(b.name);
		});

		return chainList;
	}, [chains, selectedReceivingAsset]);

	// Chain selection
	const [selectedChainId, setSelectedChainId] = useState<string | null>(null);

	const selectedChain = useMemo(
		() => chainOptions.find((c) => c.id === selectedChainId) ?? null,
		[chainOptions, selectedChainId]
	);

	// Auto-correct chain when receiving asset changes
	useEffect(() => {
		if (!selectedReceivingAsset) return;

		if (
			selectedChainId &&
			selectedReceivingAsset.availableChainIds.includes(selectedChainId)
		) {
			return;
		}

		const ethChainId = '1';
		if (selectedReceivingAsset.availableChainIds.includes(ethChainId)) {
			setSelectedChainId(ethChainId);
			return;
		}

		const firstAvailable = chainOptions.find((c) => c.isAvailable);
		if (firstAvailable) {
			setSelectedChainId(firstAvailable.id);
		}
	}, [selectedReceivingAsset, selectedChainId, chainOptions]);

	const handleChainSelect = useCallback(
		(chainId: string) => {
			const chain = chainOptions.find((c) => c.id === chainId);
			if (chain?.isAvailable) {
				setSelectedChainId(chainId);
			}
		},
		[chainOptions]
	);

	// Recipient address
	const [recipientAddress, setRecipientAddress] = useState('');
	const isValidRecipientAddress = useMemo(
		() => isValidAddressForChain(recipientAddress, selectedChainId),
		[recipientAddress, selectedChainId]
	);

	// Quote fetching — use native SOL address for SOL, SPL mint for other tokens
	const isNativeSol = selectedCollateralMarket.mint.equals(WRAPPED_SOL_MINT);
	const solanaTokenAddress = isNativeSol
		? SOLANA_TARGET_ASSETS.SOL.address
		: selectedCollateralMarket.mint.toString();

	const debouncedFetchQuote = useDebouncedCallback(
		(params: {
			fromTokenAddress: string;
			toChainId: string;
			toTokenAddress: string;
			fromAmountBaseUnit: string;
		}) => {
			fetchQuote(
				{
					fromChainId: SOLANA_CHAIN_ID,
					fromTokenAddress: params.fromTokenAddress,
					toChainId: params.toChainId,
					toTokenAddress: params.toTokenAddress,
					fromAmountBaseUnit: params.fromAmountBaseUnit,
					recipientAddress: '0x0000000000000000000000000000000000000001',
				},
				2
			).catch(() => {
				// Silently fail after all retries — UI shows fallback
			});
		},
		500
	);

	const amountBaseStr = amountBigNum.val.toString();

	useEffect(() => {
		if (
			!solanaTokenAddress ||
			!selectedChainId ||
			!selectedReceivingAsset ||
			amountBigNum.lte(BigNum.zero(selectedCollateralMarket.precisionExp), true)
		) {
			clearQuote();
			return;
		}

		const toTokenAddress =
			selectedReceivingAsset.addressByChainId[selectedChainId];
		if (!toTokenAddress) return;

		debouncedFetchQuote({
			fromTokenAddress: solanaTokenAddress,
			toChainId: selectedChainId,
			toTokenAddress,
			fromAmountBaseUnit: amountBaseStr,
		});
	}, [
		solanaTokenAddress,
		selectedChainId,
		selectedReceivingAsset,
		amountBaseStr,
		debouncedFetchQuote,
		clearQuote,
	]);

	// Submit
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { withdrawToUda } = useWithdrawToUda();

	const selectedToTokenAddress =
		selectedChainId && selectedReceivingAsset
			? selectedReceivingAsset.addressByChainId[selectedChainId]
			: undefined;

	const MIN_WITHDRAWAL_USD = 30;
	const isBelowMinimumWithdrawal =
		amountBigNum.gt(BigNum.zero(selectedCollateralMarket.precisionExp), true) &&
		quote != null &&
		quote.estInputUsd < MIN_WITHDRAWAL_USD;

	const canSubmit =
		isValidRecipientAddress &&
		amountBigNum.gt(BigNum.zero(selectedCollateralMarket.precisionExp), true) &&
		!isBelowMinimumWithdrawal &&
		!isGettingQuote &&
		!!quote &&
		!!selectedChainId &&
		!!selectedReceivingAsset &&
		!!selectedToTokenAddress &&
		!isSubmitting;

	const handleSubmit = useCallback(async () => {
		if (!canSubmit || !selectedReceivingAsset || !selectedChainId) return false;

		const toTokenAddress =
			selectedReceivingAsset.addressByChainId[selectedChainId];
		if (!toTokenAddress) return false;

		setIsSubmitting(true);
		try {
			const udaResponse = await createWithdrawalUda({
				toChainId: selectedChainId,
				toTokenAddress,
				toTokenTicker: selectedReceivingAsset.symbol,
				recipientAddress,
				sourceTokenAddress: solanaTokenAddress,
				sourceTokenSymbol: selectedCollateralMarket.symbol,
				subAccountId,
			});

			if (!udaResponse?.solanaAddr) {
				throw new Error('Failed to create withdrawal UDA');
			}

			const result = await withdrawToUda({
				amount: amountBigNum,
				spotMarketConfig: selectedCollateralMarket,
				udaSolanaAddress: udaResponse.solanaAddr,
			});

			return result;
		} catch (e) {
			console.error('Withdrawal to other destination failed:', e);
			notify({
				type: 'error',
				message: 'Withdrawal failed. Please try again.',
			});
			return false;
		} finally {
			setIsSubmitting(false);
		}
	}, [
		canSubmit,
		selectedReceivingAsset,
		selectedChainId,
		recipientAddress,
		amountBigNum,
		selectedCollateralMarket,
		withdrawToUda,
	]);

	// Convert estToTokenBaseUnit to human-readable token amount
	const estimatedReceiveAmount = useMemo(() => {
		if (!quote?.estToTokenBaseUnit || !selectedReceivingAsset) return undefined;
		const decimals = selectedReceivingAsset.decimals;
		const raw = BigInt(quote.estToTokenBaseUnit);
		const divisor = BigInt(10 ** decimals);
		const whole = raw / divisor;
		const remainder = raw % divisor;
		const fractionStr = remainder.toString().padStart(decimals, '0');
		return parseFloat(`${whole}.${fractionStr}`);
	}, [quote?.estToTokenBaseUnit, selectedReceivingAsset]);

	return {
		// Source asset filtering
		supportedSourceBanks,

		// Asset & chain selection
		uniqueAssets,
		selectedReceivingAssetSymbol: effectiveReceivingSymbol,
		setSelectedReceivingAssetSymbol,
		chainOptions,
		selectedChainId,
		setSelectedChainId,
		handleChainSelect,
		selectedChain,

		// Quote
		quote,
		isGettingQuote,
		estimatedReceiveAmount,

		// Recipient
		recipientAddress,
		setRecipientAddress,
		isValidRecipientAddress,

		// Submit
		isSubmitting,
		canSubmit,
		isBelowMinimumWithdrawal,
		handleSubmit,
	};
}
